/**
 * Safe postMessage utility for Android WebView compatibility
 * 
 * Problem: Android WebView (Instagram, Facebook in-app browsers) can garbage collect
 * Java bridge objects while JavaScript is still running, causing postMessage to fail
 * with "Java object is gone" error.
 * 
 * Solution: Wrap postMessage calls in try-catch to gracefully handle platform limitations.
 * 
 * Usage:
 *   import { safePostMessage } from '@charitystack/shared';
 *   safePostMessage(window.parent, message, '*');
 * 
 * @param {Window} targetWindow - Window to send message to (usually window.parent)
 * @param {object} message - Message object to send
 * @param {string} targetOrigin - Target origin (usually '*' for cross-origin)
 * @param {object} options - Optional configuration
 * @param {boolean} options.silent - If true, suppress console logs (default: true)
 * @returns {boolean} True if message sent successfully, false if failed
 */
export const safePostMessage = (targetWindow, message, targetOrigin = '*', options = {}) => {
  const { silent = true } = options;

  // Validation: Check if target window exists
  if (!targetWindow) {
    if (!silent) {
      console.warn('[safePostMessage] Target window is null/undefined');
    }
    return false;
  }

  // Validation: Check if window is not same as current (prevent self-messaging in non-iframe contexts)
  if (targetWindow === window) {
    if (!silent) {
      console.warn('[safePostMessage] Target window is same as current window (not in iframe)');
    }
    return false;
  }

  // Attempt postMessage with error handling
  try {
    targetWindow.postMessage(message, targetOrigin);
    return true;
  } catch (error) {
    // Expected errors in Android WebView and other restrictive environments:
    // - "Java object is gone" - Android WebView garbage collection
    // - "SecurityError" - Cross-origin restrictions
    // - "DataCloneError" - Message serialization failure
    
    if (!silent) {
      console.warn('[safePostMessage] Failed to send message:', {
        error: error.message,
        action: message?.action,
        sender: message?.sender,
        receiver: message?.receiver,
      });
    }

    // Return false to indicate failure (caller can decide how to handle)
    return false;
  }
};

/**
 * Safe postMessage specifically for parent window communication
 * Common pattern: Forms/widgets sending messages to parent/host
 *
 * @param {object} message - Message object to send
 * @param {string} targetOrigin - Target origin (default: '*')
 * @param {object} options - Optional configuration
 * @returns {boolean} True if message sent successfully, false if failed
 */
export const safePostMessageToParent = (message, targetOrigin = '*', options = {}) => {
  return safePostMessage(window.parent, message, targetOrigin, options);
};

/**
 * Post a message to `targetWindow` when it's a different window (iframe context),
 * or fall back to `window.postMessage` on the current window when it's the same
 * (hosted-page context, where the form is rendered directly in the page).
 *
 * Use this when the same form can be embedded in an iframe (window.top/parent !== window)
 * OR rendered directly in a host page (window.top/parent === window). safePostMessage()
 * refuses self-targeted posts and would silently drop the message in the same-window case.
 *
 * @param {Window} targetWindow - Preferred target (e.g. window.top, window.parent)
 * @param {object} message - Message object to send
 * @param {string} targetOrigin - Target origin (default: '*')
 * @returns {boolean} True if message dispatched, false if both paths failed
 */
export const safePostMessageWithSelfFallback = (targetWindow, message, targetOrigin = '*') => {
  if (targetWindow && targetWindow !== window) {
    return safePostMessage(targetWindow, message, targetOrigin);
  }
  try {
    window.postMessage(message, targetOrigin);
    return true;
  } catch (error) {
    console.error('[safePostMessage] Same-window postMessage failed:', {
      error: error.message,
      action: message?.action,
      sender: message?.sender,
    });
    return false;
  }
};
